import { useState, useEffect, useCallback, useMemo } from 'react';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { Product } from 'types/graphql-overrides';
import { PRODUCT_GROUP_SIZES } from './product-list.constants';

type GroupProductsByRowsArgs = {
  products: Array<Product>;
  isListView: boolean;
  rows: Array<number>;
};

type GroupProductsByRows = {
  getProductsGroup: (groupIndex: number) => Array<[Product, number]>;
  groupedProducts: Array<Array<Product>>;
};

export function useGroupProductsByRows({ products, isListView, rows }: GroupProductsByRowsArgs): GroupProductsByRows {
  const productsCount = products.length;
  const [productsPerRow, setProductsPerRow] = useState(1);
  const { sm, md, lg, xl, xxl } = PRODUCT_GROUP_SIZES.grid;

  const isSmall = useMediaQuery(`(min-width:${sm.breakpoint}px)`);
  const isMedium = useMediaQuery(`(min-width:${md.breakpoint}px)`);
  const isLarge = useMediaQuery(`(min-width:${lg.breakpoint}px)`);
  const isXLarge = useMediaQuery(`(min-width:${xl.breakpoint}px)`);
  const isXXLarge = useMediaQuery(`(min-width:${xxl.breakpoint}px)`);

  useEffect(() => {
    if (isListView) {
      setProductsPerRow(1);
    } else if (isXXLarge) {
      setProductsPerRow(xxl.rowSize);
    } else if (isXLarge) {
      setProductsPerRow(xl.rowSize);
    } else if (isLarge) {
      setProductsPerRow(lg.rowSize);
    } else if (isMedium) {
      setProductsPerRow(md.rowSize);
    } else if (isSmall) {
      setProductsPerRow(sm.rowSize);
    }
  }, [
    isLarge,
    isListView,
    isMedium,
    isSmall,
    isXLarge,
    isXXLarge,
    lg.rowSize,
    md.rowSize,
    productsCount,
    sm.rowSize,
    xl.rowSize,
    xxl.rowSize,
  ]);

  const groupedProducts = useMemo(() => {
    const groups: Array<Array<Product>> = [];
    let startIndex = 0;

    [...rows, products.length].forEach((row) => {
      const endIndex = startIndex + productsPerRow * row;

      groups.push(products.slice(startIndex, endIndex));
      startIndex = endIndex;
    });

    return groups;
  }, [rows, products, productsPerRow]);

  const getProductsGroup = useCallback(
    (groupIndex: number): Array<[Product, number]> => {
      const relativeIndex = groupedProducts.slice(0, groupIndex).reduce((acc, curr) => acc + curr.length, 0);

      return groupedProducts[groupIndex].map((product, index): [Product, number] => [product, index + relativeIndex]);
    },
    [groupedProducts]
  );

  return {
    getProductsGroup,
    groupedProducts,
  };
}
